import cn from 'clsx';
import { isFuture, isPast } from 'date-fns/esm';
import format from 'date-fns/format';

import PollPledgeIcon from '@components/modules/Poll/PollPledgeIcon';
import { PollStatus } from '@sdk/models';
import { fetchPollDetails, FormattedPoll } from '@utils/Poll';
import { LockIcon } from '../Icons/Lock';
import { useEffect, useState } from 'react';

export interface PollListRowProps
  extends React.ComponentPropsWithoutRef<'div'> {
  align?: 'left' | 'right';
  index: number;
  poll: FormattedPoll;
}

const BG_COLORS = [
  'bg-yellow',
  'bg-magenta',
  'bg-orange',
  'bg-green',
  'bg-blue-light',
  'bg-salmon',
  'bg-purple',
  'bg-yellow',
  'bg-red-pastel',
];

const PollListRow: React.FC<PollListRowProps> = ({
  className,
  align = 'left',
  index,
  poll,
  ...props
}) => {
  // Get color, limit by length and carousell
  let bgIndex = index;
  while (bgIndex >= BG_COLORS.length) {
    bgIndex = bgIndex - BG_COLORS.length;
  }

  const rootCn = cn(
    'flex cursor-pointer',
    BG_COLORS[bgIndex],
    {
      'h-56 sm:h-48': poll.title.length <= 30,
      'h-72 sm:h-64': poll.title.length > 30 && poll.title.length <= 50,
      'h-96 sm:h-72': poll.title.length > 50,
      'flex-row-reverse': align === 'right',
    },
    className
  );

  let pollStatus: 'Upcoming' | 'Ended' | 'Ongoing' | 'Pledging' = 'Ended';
  if (poll.status === PollStatus.END) {
    pollStatus = 'Ended';
  } else if (
    poll.status === PollStatus.PLEDGING1 &&
    poll.pledgePeriod1StartDate &&
    poll.pledgePeriod1EndDate
  ) {
    if (!poll.isPledgePeriod) {
      pollStatus = 'Upcoming';
    } else {
      pollStatus = 'Pledging';
    }
  } else if (poll.isVotePeriod) {
    pollStatus = 'Ongoing';
  } else if (isFuture(poll.startDate)) {
    pollStatus = 'Upcoming';
  }

  let displayDate;
  if (isPast(poll.endDate)) {
    if (poll.pledgePeriod1StartDate && poll.pledgePeriod1EndDate) {
      if (isPast(poll.pledgePeriod1EndDate)) {
        displayDate = `Ended ${format(
          poll.pledgePeriod1EndDate,
          'dd MMMM yyyy'
        )}`;
      } else if (isPast(poll.pledgePeriod1StartDate)) {
        displayDate = `Ends ${format(
          poll.pledgePeriod1EndDate,
          'dd MMMM yyyy'
        )}`;
      } else {
        displayDate = `Starts ${format(
          poll.pledgePeriod1StartDate,
          'dd MMMM yyyy'
        )}`;
      }
    } else {
      displayDate = `Ended ${format(poll.endDate, 'dd MMMM yyyy')}`;
    }
  } else if (isPast(poll.startDate)) {
    displayDate = `Ends ${format(poll.endDate, 'dd MMMM yyyy')}`;
  } else {
    displayDate = `Starts ${format(poll.startDate, 'dd MMMM yyyy')}`;
  }

  const [voteCount, setVoteCount] = useState(0);

  const getPollDetail = async () => {
    const pollDetail = await fetchPollDetails('', poll.id);
    setVoteCount(
      pollDetail.poll.voteCount +
        (pollDetail.subPolls
          ? pollDetail.subPolls.reduce((r, a) => {
              r += a.voteCount;
              return r;
            }, 0)
          : 0)
    );
  };

  useEffect(() => {
    getPollDetail();
  }, []);

  return (
    <div className={rootCn} {...props}>
      <div className="flex flex-col flex-1 w-full p-4 font-bold font-display">
        <div className="flex flex-row items-center space-x-2">
          {poll.pledgePeriod1StartTime && (
            <div className="mb-2 w-7 h-7">
              <PollPledgeIcon color="animated" />
            </div>
          )}

          <div className={cn('text-sm uppercase mb-1')}>
            <div
              className={cn('inline-block px-2 text-white ', {
                'bg-red': pollStatus === 'Pledging' || pollStatus === 'Ongoing',
                'bg-blue': pollStatus === 'Upcoming',
                'bg-gray-dark': pollStatus === 'Ended',
              })}
            >
              {pollStatus}
            </div>
          </div>

          {poll.password !== undefined && poll.password !== null && (
            <div className={cn('text-sm uppercase mb-1')}>
              <LockIcon className="w-full h-full" />
            </div>
          )}
        </div>
        <div
          className="flex flex-col mb-4 text-xl font-bold leading-6 tracking-tighter break-words"
          style={{ hyphens: 'auto', wordBreak: 'break-all' }}
        >
          {poll.title}
        </div>
        <div className="flex-1">X {voteCount} votes</div>

        {poll.company?.name && (
          <div className="flex-1">{poll.company.name}</div>
        )}
        <div className="text-xs text-gray-800 underline">{displayDate}</div>
      </div>
      <div className="w-3/5 h-full hover:w-3/5">
        <img
          className="object-cover w-full h-full"
          src={poll.image?.url}
          alt="poll"
        />
      </div>
    </div>
  );
};

export default PollListRow;
